import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CalendarIcon, CrownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SessionFeedbackForm from "@/components/profile/SessionFeedbackForm";
import SessionFeedbackDisplay from "@/components/profile/SessionFeedbackDisplay";

type Session = {
  id: string;
  session_type: string;
  company: string | null;
  level: string | null;
  scheduled_at: string | null;
  notes: string | null;
  created_at: string;
  feedback_submitted: boolean | null;
};

type Feedback = {
  id: string;
  rating: number;
  feedback_text: string | null;
  created_at: string;
  session_id: string;
};

const Profile = () => {
  const { user, isLoading, isSubscribed, isPremiumLoading } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<{start: string | null, end: string | null}>({
    start: null,
    end: null
  });
  
  useEffect(() => {
    // Redirect if not logged in
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    // Fetch subscription data
    const fetchSubscriptionInfo = async () => {
      if (!user || !isSubscribed) return;
      
      try {
        const { data, error } = await supabase
          .from('subscribers')
          .select('subscription_start, subscription_end')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setSubscriptionData({
            start: data.subscription_start,
            end: data.subscription_end
          });
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      }
    };
    
    fetchSubscriptionInfo();
  }, [user, isSubscribed]);

  useEffect(() => {
    const fetchSessionsAndFeedback = async () => {
      if (user) {
        try {
          setIsSessionsLoading(true);
          
          // Fetch sessions
          const { data: sessionsData, error: sessionsError } = await supabase
            .from('candidate_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (sessionsError) throw sessionsError;
          
          // Fetch feedback
          const { data: feedbackData, error: feedbackError } = await supabase
            .from('session_feedback')
            .select('*')
            .eq('user_id', user.id);
          
          if (feedbackError) throw feedbackError;
          
          setSessions(sessionsData || []);
          setFeedbacks(feedbackData || []);
        } catch (error) {
          console.error('Error fetching sessions and feedback:', error);
        } finally {
          setIsSessionsLoading(false);
        }
      }
    };
    
    fetchSessionsAndFeedback();
  }, [user]);

  const handleFeedbackSubmitted = () => {
    // Refresh sessions and feedback data
    const refreshData = async () => {
      if (user) {
        try {
          const { data: sessionsData, error: sessionsError } = await supabase
            .from('candidate_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (sessionsError) throw sessionsError;
          
          const { data: feedbackData, error: feedbackError } = await supabase
            .from('session_feedback')
            .select('*')
            .eq('user_id', user.id);
          
          if (feedbackError) throw feedbackError;
          
          setSessions(sessionsData || []);
          setFeedbacks(feedbackData || []);
        } catch (error) {
          console.error('Error refreshing data:', error);
        }
      }
    };
    
    refreshData();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="container max-w-5xl py-12">
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Format session type for display
  const formatSessionType = (type: string) => {
    if (type === "mock_interview") return "Mock Interview";
    if (type === "coaching") return "1:1 Coaching";
    return type;
  };

  const getFeedbackForSession = (sessionId: string) => {
    return feedbacks.find(feedback => feedback.session_id === sessionId);
  };

  const canSubmitFeedback = (session: Session) => {
    // Can't submit feedback if already submitted
    if (session.feedback_submitted) return false;
    
    // If no scheduled_at, assume it's a completed session that can receive feedback
    if (!session.scheduled_at) return true;
    
    const sessionDate = new Date(session.scheduled_at);
    const now = new Date();
    
    // Session should be completed (in the past) to allow feedback
    return sessionDate < now;
  };

  const getSessionStatus = (session: Session) => {
    const feedback = getFeedbackForSession(session.id);
    
    if (feedback) {
      return { text: "Feedback Submitted", color: "text-green-600" };
    }
    
    if (!session.scheduled_at) {
      // If no scheduled_at but can submit feedback, it's likely a completed session
      if (canSubmitFeedback(session)) {
        return { text: "Feedback Pending", color: "text-blue-600" };
      } else {
        return { text: "Completed", color: "text-green-600" };
      }
    }
    
    const sessionDate = new Date(session.scheduled_at);
    const now = new Date();
    
    if (sessionDate < now) {
      // Session is in the past
      if (canSubmitFeedback(session)) {
        return { text: "Feedback Pending", color: "text-blue-600" };
      } else {
        return { text: "Completed", color: "text-green-600" };
      }
    } else {
      // Session is in the future
      return { text: "Scheduled", color: "text-gray-500" };
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="container max-w-5xl py-12">
          <h1 className="text-3xl font-bold mb-8">My Profile</h1>

          <Tabs defaultValue="sessions" className="w-full">
            <TabsList>
              <TabsTrigger value="sessions">My Sessions</TabsTrigger>
              <TabsTrigger value="account">Account Info</TabsTrigger>
            </TabsList>
        
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>My Scheduled Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {isSessionsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : sessions.length > 0 ? (
                <div className="space-y-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session Type</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead>Scheduled For</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => {
                        const status = getSessionStatus(session);
                        
                        return (
                          <TableRow key={session.id}>
                            <TableCell>{formatSessionType(session.session_type)}</TableCell>
                            <TableCell>{session.company || "N/A"}</TableCell>
                            <TableCell>{session.level || "N/A"}</TableCell>
                            <TableCell>
                              {session.scheduled_at 
                                ? format(new Date(session.scheduled_at), "MMM d, yyyy h:mm a") 
                                : "Not scheduled yet"}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {session.notes || "No notes"}
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${status.color}`}>
                                {status.text}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {/* Feedback Forms and Displays */}
                  {sessions.map((session) => {
                    const feedback = getFeedbackForSession(session.id);
                    const canSubmit = canSubmitFeedback(session);
                    
                    if (feedback) {
                      return (
                        <div key={`feedback-${session.id}`}>
                          <h3 className="text-lg font-medium mb-2">
                            {formatSessionType(session.session_type)} - {session.scheduled_at ? format(new Date(session.scheduled_at), "MMM d, yyyy") : "TBD"}
                          </h3>
                          <SessionFeedbackDisplay 
                            feedback={feedback} 
                            sessionType={session.session_type}
                          />
                        </div>
                      );
                    }
                    
                    if (canSubmit) {
                      return (
                        <div key={`form-${session.id}`}>
                          <h3 className="text-lg font-medium mb-2">
                            {formatSessionType(session.session_type)} - {session.scheduled_at ? format(new Date(session.scheduled_at), "MMM d, yyyy") : "TBD"}
                          </h3>
                          <SessionFeedbackForm
                            sessionId={session.id}
                            sessionType={session.session_type}
                            onFeedbackSubmitted={handleFeedbackSubmitted}
                          />
                        </div>
                      );
                    }
                    
                    return null;
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">You haven't scheduled any sessions yet.</p>
                  <div className="flex justify-center gap-4">
                    <Button onClick={() => navigate('/mock-interview')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Schedule Mock Interview
                    </Button>
                    <Button onClick={() => navigate('/coaching')} variant="outline">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Book 1:1 Coaching
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-muted-foreground">{user?.email}</p>
                  </div>
                  {user?.user_metadata?.name && (
                    <div>
                      <p className="font-medium">Name</p>
                      <p className="text-muted-foreground">{user.user_metadata.name}</p>
                    </div>
                  )}
                </div>
                
                {/* Premium Membership Section */}
                <div className="border-t pt-5">
                  <h3 className="font-semibold text-lg mb-4">Membership Status</h3>
                  
                  {isPremiumLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking membership status...</span>
                    </div>
                  ) : isSubscribed ? (
                    <div className="bg-primary/10 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-primary">
                        <CrownIcon className="h-5 w-5" />
                        <span className="font-medium">Premium Member</span>
                      </div>
                      
                      {subscriptionData.end && (
                        <div>
                          <p className="text-sm text-muted-foreground">Your premium membership expires on:</p>
                          <p className="font-medium">
                            {format(new Date(subscriptionData.end), "MMMM d, yyyy")}
                          </p>
                        </div>
                      )}
                      
                      {subscriptionData.start && (
                        <div className="text-sm text-muted-foreground">
                          <p>Member since: {format(new Date(subscriptionData.start), "MMMM d, yyyy")}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <p>You are currently using a free account.</p>
                      <Button 
                        onClick={() => navigate('/pricing')} 
                        variant="outline" 
                        className="w-fit"
                      >
                        <CrownIcon className="mr-2 h-4 w-4" />
                        Upgrade to Premium
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
